import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { LogoContainer } from "./Logo_old";
import ServiceIcon from "../ui/ServiceIcon";
import "./Header.css";
import { useAppContext } from "../../AppContext";
import { toSlug } from "../../utils";
import onboardingHandshake from "../../assets/icons_services_onboarding/Onboarding - Black.svg";
import onboardingEnrollment from "../../assets/icons_services_onboarding/Payer Enrollment - Black 1.svg";
import onboardingMonitoring from "../../assets/icons_services_onboarding/Monitoring and Tracking - Black 1.svg";
import onboardingCredentialing from "../../assets/icons_services_onboarding/Provider Credentialing - Black 1.svg";
import Data_Manage from "../../assets/icons_services_onboarding/data-management - black 1.svg";
import Security_Risk from "../../assets/icons_services_onboarding/security-audit 2.svg";
import Vendor_Cred from "../../assets/icons_services_onboarding/VRA - Black 1.svg";
import onboardingHandshakeGreen from "../../assets/icons_services_onboarding/OnBoarding - Dark Green.svg";
import onboardingEnrollmentGreen from "../../assets/icons_services_onboarding/Payer Enrollment - Dark Green.svg";
import onboardingMonitoringGreen from "../../assets/icons_services_onboarding/Monitoring and Tracking - Dark Green.svg";
import onboardingCredentialingGreen from "../../assets/icons_services_onboarding/Provider Credentialing - Dark Green.svg";
import Data_ManageGreen from "../../assets/icons_services_onboarding/data-management - Dark Green.svg";
import Security_RiskGreen from "../../assets/icons_services_onboarding/SRA - Dark Green.svg";
import Vendor_CredGreen from "../../assets/icons_services_onboarding/VRA - Dark Green.svg";
import Healthcare_Providers_Black from "../../assets/icons_services_whoweserve/Healthcare_Providers_White.png";
import Healthcare_Support_Services_Black from "../../assets/icons_services_whoweserve/Healthcare_Support_Services_Black.png";
import { createPathProto } from "utils";
import LogoHorizontal from "../../assets/images/icrenditialing-logo.svg";

// Hook for dropdown hover management
const useDropdownHover = (dropdownRefs) => {
  const [activeDropdown, setActiveDropdown] = useState(null);
  const timersRef = useRef({});
  const isHoveringRef = useRef(false);

  useEffect(() => {
    return () =>
      Object.values(timersRef).forEach((timer) => timer && clearTimeout(timer));
  }, []);

  const createMouseHandlers = useCallback(
    (dropdownId) => {
      return {
        handleMouseEnter: () => {
          Object.keys(timersRef.current).forEach((key) => {
            if (timersRef.current[key]) {
              clearTimeout(timersRef.current[key]);
              timersRef.current[key] = null;
            }
          });
          isHoveringRef.current = true;
          setActiveDropdown(dropdownId);
        },
        handleMouseLeave: (e) => {
          const relatedTarget = e.relatedTarget;
          const isMovingToDropdownElement =
            relatedTarget &&
            Object.keys(dropdownRefs).some((key) => {
              const ref = dropdownRefs[key].current;
              return (
                ref &&
                (ref.contains(relatedTarget) ||
                  ref.closest(".header-dropdown") === relatedTarget ||
                  relatedTarget.closest(".header-dropdown"))
              );
            });
          if (isMovingToDropdownElement) return;
          timersRef.current[dropdownId] = setTimeout(() => {
            if (!isHoveringRef.current) setActiveDropdown(null);
          }, 300);
          isHoveringRef.current = false;
        },
      };
    },
    [dropdownRefs]
  );

  useEffect(() => {
    const handlers = {};
    Object.entries(dropdownRefs).forEach(([dropdownId, ref]) => {
      if (ref.current) {
        const { handleMouseEnter, handleMouseLeave } =
          createMouseHandlers(dropdownId);
        handlers[dropdownId] = { handleMouseEnter, handleMouseLeave };
        ref.current.addEventListener("mouseenter", handleMouseEnter);
        ref.current.addEventListener("mouseleave", handleMouseLeave);
      }
    });
    return () => {
      Object.entries(dropdownRefs).forEach(([dropdownId, ref]) => {
        if (ref.current && handlers[dropdownId]) {
          ref.current.removeEventListener(
            "mouseenter",
            handlers[dropdownId].handleMouseEnter
          );
          ref.current.removeEventListener(
            "mouseleave",
            handlers[dropdownId].handleMouseLeave
          );
        }
      });
    };
  }, [dropdownRefs, createMouseHandlers]);

  return [activeDropdown, setActiveDropdown];
};

const Header = () => {
  const headerRef = useRef(null);
  const menuRef = useRef(null);
  const { isMobile } = useAppContext();
  const servicesMenuRef = useRef(null);
  const whoWeServeMenuRef = useRef(null);
  const servicesDropdownRef = useRef(null);
  const whoWeServeDropdownRef = useRef(null);

  const servicesProto = createPathProto("/services/");
  const healthCareProto = createPathProto("/who-we-serve/");

  const [hoveredService, setHoveredService] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const dropdownRefs = {
    services: servicesDropdownRef,
    "services-menu": servicesMenuRef,
    whoWeServe: whoWeServeDropdownRef,
    "whoWeServe-menu": whoWeServeMenuRef,
  };

  const [activeDropdown, setActiveDropdown] = useDropdownHover(dropdownRefs);

  const isServicesHovered =
    activeDropdown === "services" || activeDropdown === "services-menu";
  const isWhoWeServeHovered =
    activeDropdown === "whoWeServe" || activeDropdown === "whoWeServe-menu";

  const handleNavigation = useCallback(() => {
    setIsMobileMenuOpen(false);
    setActiveDropdown(null);
  }, [setActiveDropdown]);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (headerRef.current && !headerRef.current.contains(e.target))
        handleNavigation();
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [handleNavigation]);

  const services = [
    {
      name: "Onboarding",
      icon: onboardingHandshake,
      selectedIcon: onboardingHandshakeGreen,
    },
    {
      name: "Provider Credentialing",
      icon: onboardingCredentialing,
      selectedIcon: onboardingCredentialingGreen,
    },
    {
      name: "Payor Enrollment",
      icon: onboardingEnrollment,
      selectedIcon: onboardingEnrollmentGreen,
    },
    {
      name: "Monitoring and Tracking",
      icon: onboardingMonitoring,
      selectedIcon: onboardingMonitoringGreen,
    },
    {
      name: "Security Risk Assessment",
      icon: Security_Risk,
      selectedIcon: Security_RiskGreen,
    },
    {
      name: "Vendor Risk Assessment",
      icon: Vendor_Cred,
      selectedIcon: Vendor_CredGreen,
    },
    {
      name: "Data Exchange Management",
      icon: Data_Manage,
      selectedIcon: Data_ManageGreen,
    },
  ].map((obj) => {
    obj.targetId = `section-${toSlug(obj.name)}`;
    return Object.setPrototypeOf(obj, servicesProto);
  });

  const healthCare = [
    {
      id: 0,
      name: "Healthcare Providers",
      icon: Healthcare_Providers_Black,
      targetId: "section-healthcare-providers",
    },
    {
      id: 1,
      name: "Healthcare Support Services",
      icon: Healthcare_Support_Services_Black,
      targetId: "section-healthcare-support-services",
    },
  ].map((obj) => Object.setPrototypeOf(obj, healthCareProto));

  function getServiceIcon(icon, size = 30) {
    if (typeof icon === "string" && icon.includes("/"))
      return <img src={icon} alt="" style={{ width: size, height: size }} />;
    else if (icon && typeof icon === "object")
      return <img src={icon} alt="" style={{ width: size, height: size }} />;
    else return <ServiceIcon type={icon} size={size} />;
  }

  // Scroll helper
  const HEADER_OFFSET = 90; // adjust to match your header height

  const scrollToSection = (sectionId) => {
    if (!sectionId) return;
    const el = document.getElementById(sectionId);
    if (!el) return;

    requestAnimationFrame(() => {
      const elementTop = el.getBoundingClientRect().top + window.scrollY;
      const offsetPosition = elementTop - HEADER_OFFSET;
      window.scrollTo({ top: offsetPosition, behavior: "smooth" });
    });
  };

  const handleNavigateAndScroll = (item) => {
    handleNavigation();
    if (item?.targetId) scrollToSection(item.targetId);
  };

  // Desktop Who We Serve
  const renderDesktopWhoWeServices = () => (
    <div className="header-dropdown-men" ref={whoWeServeDropdownRef}>
      <div className="dropdown-inner-container">
        <div className="header-who-we-serve-grid">
          {healthCare.map((category) => (
            <div key={category.id} className="header-who-we-serve-column">
              <Link
                key={category.id}
                to={
                  healthCare.find((ct) => ct.name === category.name)?.path ||
                  "/"
                }
                onClick={() =>
                  handleNavigateAndScroll(
                    healthCare.find((ct) => ct.name === category.name)
                  )
                }
                className={`header-dropdown-item-who-we-serve ${
                  hoveredService === category.id ? "hovered" : ""
                }`}
                onMouseEnter={() => setHoveredService(category.id)}
                onMouseLeave={() => setHoveredService(null)}
              >
                <div className="header-service-icon">
                  {getServiceIcon(category.icon)}
                </div>
                <h4 className="who-we-serve-category">{category.name}</h4>
              </Link>
            </div>
          ))}
        </div>
      </div>
      <div className="dropdown-hover-area" />
    </div>
  );

  const renderMobileWhoWeServices = () => (
    <ul className="mobile-nav-list">
      {healthCare.map((client, idx) => (
        <Link
          key={idx}
          to={client.path || "/"}
          onClick={() => handleNavigateAndScroll(client)}
          className="header-dropdown-item"
        >
          <div className="header-service-icon">
            {getServiceIcon(client.icon)}
          </div>
          <span className="header-item-name mobile">{client.name}</span>
        </Link>
      ))}
    </ul>
  );

  // Desktop Services
  const renderDesktopServices = () => (
    <div className="header-dropdown-menu" ref={servicesDropdownRef}>
      <div className="dropdown-inner-container">
        <div className="header-services-grid">
          {[0, 1, 2].map((columnIdx) => (
            <div key={columnIdx} className="header-services-column">
              {services
                .filter((_, idx) => idx % 3 === columnIdx)
                .map((service) => {
                  const idx = services.indexOf(service);
                  return (
                    <Link
                      key={idx}
                      to={service.path}
                      onClick={() => handleNavigation()}
                      className={`header-dropdown-item ${
                        hoveredService === idx ? "hovered" : ""
                      }`}
                      onMouseEnter={() => setHoveredService(idx)}
                      onMouseLeave={() => setHoveredService(null)}
                    >
                      <div className="header-service-icon">
                        {getServiceIcon(
                          hoveredService === idx
                            ? service.selectedIcon
                            : service.icon
                        )}
                      </div>
                      <span className="header-item-name">{service.name}</span>
                    </Link>
                  );
                })}
            </div>
          ))}
        </div>
      </div>
      <div className="dropdown-hover-area" />
    </div>
  );

  const renderMobileServices = () => (
    <ul className="mobile-nav-list">
      {services.map((service, idx) => (
        <Link
          key={idx}
          to={service.path}
          onClick={() => handleNavigateAndScroll(service)}
          className="header-dropdown-item"
        >
          <div className="header-service-icon">
            {getServiceIcon(service.icon)}
          </div>
          <span className="header-item-name mobile">{service.name}</span>
        </Link>
      ))}
    </ul>
  );

  return (
    <header className="header-container" ref={headerRef}>
      {isMobile && (
        <>
          <div className="mobile-header-wrapper">
            <Link to="/" onClick={handleNavigation}>
              <LogoContainer className="logo-container">
                <img
                  src={LogoHorizontal}
                  alt="Credentialing"
                  className="header-logo-img"
                />
              </LogoContainer>
            </Link>
            <label htmlFor="menu-toggle" className="menu-toggle">
              {isMobileMenuOpen
                ? getServiceIcon("close-mobile-menu-icon", 30, "#333")
                : getServiceIcon("open-mobile-menu-icon", 32, "#333")}
            </label>
          </div>
          <input
            type="checkbox"
            checked={isMobileMenuOpen}
            id="menu-toggle"
            className="menu-checkbox"
            onChange={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          />
        </>
      )}

      <div className="container" ref={menuRef}>
        <div className="header-content-wrapper">
          {!isMobile && (
            <div className="header-logo">
              <Link to="/" onClick={handleNavigation}>
                <LogoContainer className="logo-container">
                  <img
                    src={LogoHorizontal}
                    alt="Credentialing"
                    className="header-logo-img"
                  />
                </LogoContainer>
              </Link>
            </div>
          )}

          <nav className="header-nav main-nav">
            <ul>
              <li className="header-nav-item">
                <Link
                  to="/"
                  onClick={handleNavigation}
                  className="header-nav-link"
                >
                  Home
                </Link>
              </li>

              <li
                className={`header-nav-item header-dropdown ${
                  isWhoWeServeHovered ? "active dropdown-active" : ""
                }`}
                onMouseEnter={() =>
                  !isMobile && setActiveDropdown("whoWeServe-menu")
                }
                ref={whoWeServeMenuRef}
              >
                <Link
                  to="/who-we-serve"
                  className="header-nav-link"
                  onClick={(e) => {
                    if (isMobile) {
                      e.preventDefault();
                      setActiveDropdown(
                        isWhoWeServeHovered ? null : "whoWeServe-menu"
                      );
                    } else handleNavigation();
                  }}
                >
                  <span
                    className={`${
                      isWhoWeServeHovered ? "dropdown-active" : ""
                    }`}
                  >
                    Who We Serve
                  </span>
                  {isMobile &&
                    (isWhoWeServeHovered
                      ? getServiceIcon(
                          "up-arrow-menu-icon",
                          undefined,
                          "#003F4D"
                        )
                      : getServiceIcon(
                          "down-arrow-menu-icon",
                          undefined,
                          "black"
                        ))}
                </Link>

                {isWhoWeServeHovered &&
                  (isMobile
                    ? renderMobileWhoWeServices()
                    : renderDesktopWhoWeServices())}
              </li>

              <li
                className={`header-nav-item header-dropdown ${
                  isServicesHovered ? "active dropdown-active" : ""
                }`}
                onMouseEnter={() =>
                  !isMobile && setActiveDropdown("services-menu")
                }
                ref={servicesMenuRef}
              >
                <Link
                  to="/services/onboarding"
                  className="header-nav-link"
                  onClick={(e) => {
                    if (isMobile) {
                      e.preventDefault();
                      setActiveDropdown(
                        isServicesHovered ? null : "services-menu"
                      );
                    } else handleNavigation();
                  }}
                >
                  <span
                    className={`${isServicesHovered ? "dropdown-active" : ""}`}
                  >
                    Services
                  </span>
                  {isMobile &&
                    (isServicesHovered
                      ? getServiceIcon(
                          "up-arrow-menu-icon",
                          undefined,
                          "#003F4D"
                        )
                      : getServiceIcon(
                          "down-arrow-menu-icon",
                          undefined,
                          "black"
                        ))}
                </Link>

                {isServicesHovered &&
                  (isMobile ? renderMobileServices() : renderDesktopServices())}
              </li>

              <li className="header-nav-item">
                <Link
                  to="/contact"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="header-nav-link"
                >
                  Contact
                </Link>
              </li>
            </ul>
          </nav>

          <div className="header-buttons-container">
            <motion.button
              className="header-login-button"
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => (window.location.href = "/login")}
            >
              Login
            </motion.button>

            <motion.button
              className="header-demo-button"
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => (window.location.href = "/schedule-demo")}
            >
              Schedule a Meet
            </motion.button>
          </div>
        </div>
      </div>

      {(isServicesHovered || isWhoWeServeHovered) && !isMobile && (
        <div className="dropdown-backdrop" onClick={handleNavigation} />
      )}
    </header>
  );
};

export default Header;
